// 手牌（THand）結構與操作
// 對應 PRD §13.2 THand、§8.1 自動理牌
//
// 設計原則：
// - 不可變資料，所有操作回傳新 THand
// - concealed 永遠維持已排序狀態（PRD §8.1：每次變動後自動理牌）
// - drawn tile 不另設欄位 — 抓進的牌直接併入 concealed 並排序，
//   UI 要高亮「剛抓進的那張」就靠 tile.drawn 事件 payload，而非 THand 狀態
//
// 為什麼這樣設計？避免 Core 與 UI 對「drawn 是什麼狀態」的雙來源真相，
// 簡化 CRound 狀態機判斷。

import type { TMeld } from './meld';
import { compareTiles, isFlower, type TTile } from './tile';

/**
 * 一家的手牌完整狀態。
 *
 * - `concealed`：暗牌區（含剛抓進的牌），已排序
 * - `melds`：已副露的面子（吃/碰/槓）
 * - `flowers`：花牌區（補花後的花牌全部累積在這）
 */
export type THand = {
  readonly concealed: readonly TTile[];
  readonly melds: readonly TMeld[];
  readonly flowers: readonly TTile[];
};

/** 建立一個空手牌（發牌前的初始狀態）。 */
export const createEmptyHand = (): THand => ({
  concealed: [],
  melds: [],
  flowers: [],
});

/**
 * 把一張牌加進暗牌區，自動排序。
 * 抓牌、發牌、補花後抓進的非花牌都走這裡。
 */
export const addToConcealed = (hand: THand, tile: TTile): THand => ({
  ...hand,
  concealed: [...hand.concealed, tile].sort(compareTiles),
});

/**
 * 把一張花牌加進花牌區，自動排序。
 * 補花流程：抓到花 → 加進 flowers → 再抓一張替補（替補可能還是花，迴圈直到非花）。
 */
export const addToFlowers = (hand: THand, tile: TTile): THand => ({
  ...hand,
  flowers: [...hand.flowers, tile].sort(compareTiles),
});

/**
 * 從暗牌移除一張（依 id 比對，移除唯一那張）。
 * 找不到時回傳原 hand，不報錯（呼叫端應確保牌存在）。
 *
 * 注意用 id 比對而不是 isSameKind — 因為「拿掉特定那張」很重要：
 * 例如手中有兩張 5 萬，玩家點選的是 id=42 那張，要拿掉的就是 id=42。
 */
export const removeFromConcealed = (hand: THand, tile: TTile): THand => {
  const idx = hand.concealed.findIndex((t) => t.id === tile.id);
  if (idx < 0) return hand;
  return {
    ...hand,
    concealed: [...hand.concealed.slice(0, idx), ...hand.concealed.slice(idx + 1)],
  };
};

/**
 * 抓進一張牌，自動分流：
 * - 是花牌 → 進 flowers，回傳 isFlower=true（呼叫端要再抓一張替補）
 * - 不是花 → 進 concealed，回傳 isFlower=false（流程結束）
 *
 * 這個函式只處理「分流」，不負責補花的迴圈本身（迴圈在 CRound 狀態機裡跑）。
 */
export const intakeTile = (hand: THand, tile: TTile): { hand: THand; isFlower: boolean } => {
  if (isFlower(tile)) {
    return { hand: addToFlowers(hand, tile), isFlower: true };
  }
  return { hand: addToConcealed(hand, tile), isFlower: false };
};

/** 加一個副露面子（吃/碰/槓完成後）。 */
export const addMeld = (hand: THand, meld: TMeld): THand => ({
  ...hand,
  melds: [...hand.melds, meld],
});

/**
 * 計算手牌總張數（暗 + 副露 + 花）。
 *
 * 用途：
 * - 補花後驗證：莊家應有 17 張（補花之外）、閒家 16 張
 * - 出牌後驗證：當前家應有 16 張暗+副露總數（不含花）
 *
 * 注意 melds 內每個 meld 可能 3 張或 4 張（槓），所以要逐個累加。
 */
export const totalTileCount = (hand: THand): number =>
  hand.concealed.length +
  hand.melds.reduce((s, m) => s + m.tiles.length, 0) +
  hand.flowers.length;

/**
 * 計算手牌「可用張數」（暗 + 副露，不含花）。
 *
 * 用途：判定當前家是該抓牌還是該出牌：
 * - 等於 15 + melds * 3 → 該抓牌（差一張）
 * - 等於 16 + melds * 3 → 該出牌
 * （莊家初始 17，閒家初始 16；含副露時各家總和維持 16）
 *
 * MVP 主要當斷言用，正常流程不直接用這判斷。
 */
export const playableTileCount = (hand: THand): number =>
  hand.concealed.length + hand.melds.reduce((s, m) => s + m.tiles.length, 0);
